import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, API } from '../lib/api';
import { Input } from '../components/ui/input';
import { EnvelopeSimple, Phone, ShieldCheck, IdentificationCard } from '@phosphor-icons/react';

const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

const printIdCards = (staffList, branding) => {
  const cards = staffList.map((u) => `
    <div class="card">
      <div class="head"><img class="logo" src="${API}/branding/file/logo" onerror="this.style.display='none'"/><span>${esc(branding?.school_name || 'ONRID School')}</span></div>
      <div class="body">
        <img class="photo" src="${API}/people/user/${esc(u.id)}/photo" onerror="this.style.display='none';this.nextElementSibling.style.display='flex'"/><div class="ph" style="display:none">${esc(u.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase())}</div>
        <div><p class="name">${esc(u.name)}</p><p class="role">${esc(u.role.replace(/_/g, ' '))}</p>
        <p class="meta">${esc(u.email)}</p>${u.phone ? `<p class="meta">${esc(u.phone)}</p>` : ''}
        <p class="bgv ${u.bgv_status === 'approved' ? 'ok' : ''}">BGV: ${esc(u.bgv_status.replace(/_/g, ' '))}${u.bgv_valid_until ? ` · valid till ${esc(u.bgv_valid_until)}` : ''}</p></div>
      </div>
      <div class="foot">STAFF ID · ${esc(u.id.slice(0, 8).toUpperCase())}</div>
    </div>`).join('');
  const html = `<!DOCTYPE html><html><head><title>Staff ID Cards</title><style>
    body{font-family:Arial,sans-serif;display:flex;flex-wrap:wrap;gap:16px;padding:16px}
    .card{width:336px;border:2px solid #EA580C;border-radius:12px;overflow:hidden;page-break-inside:avoid}
    .head{background:#0F172A;color:#fff;padding:8px 12px;font-size:13px;font-weight:bold;display:flex;align-items:center;gap:8px}
    .logo{height:22px}
    .body{display:flex;gap:12px;padding:12px;align-items:center}
    .photo{width:72px;height:72px;border-radius:50%;object-fit:cover;border:2px solid #FED7AA}
    .ph{width:72px;height:72px;border-radius:50%;background:#FFF7ED;border:2px solid #FED7AA;display:flex;align-items:center;justify-content:center;font-weight:bold;color:#C2410C;font-size:22px}
    .name{margin:0;font-size:16px;font-weight:bold}.role{margin:2px 0;font-size:11px;text-transform:uppercase;color:#EA580C;letter-spacing:.5px}
    .meta{margin:1px 0;font-size:10px;color:#475569}
    .bgv{margin:4px 0 0;font-size:9px;text-transform:uppercase;background:#F1F5F9;color:#64748B;display:inline-block;padding:2px 6px;border-radius:4px}
    .bgv.ok{background:#DCFCE7;color:#166534}
    .foot{background:#FFF7ED;padding:4px 12px;font-size:9px;color:#9A3412;letter-spacing:1px}
    @media print{body{gap:8px}}
  </style></head><body onload="setTimeout(()=>window.print(),400)">${cards}</body></html>`;
  const url = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  window.open(url, '_blank');
  setTimeout(() => URL.revokeObjectURL(url), 30000);
};

const ROLE_LABEL = { school_admin: 'School Admin', principal: 'Principal', registrar: 'Registrar', admissions_officer: 'Admissions', teacher: 'Teacher', finance_admin: 'Finance' };
const BGV_STYLE = { approved: 'bg-green-100 text-green-800', rejected: 'bg-rose-100 text-rose-700', in_progress: 'bg-amber-100 text-amber-800', pending_decision: 'bg-amber-100 text-amber-800', not_started: 'bg-slate-100 text-slate-500' };

const Avatar = ({ u }) => {
  const [broken, setBroken] = useState(false);
  if (u.has_photo && !broken) {
    return <img src={`${API}/people/user/${u.id}/photo`} alt={u.name} onError={() => setBroken(true)}
      className="w-16 h-16 rounded-full object-cover border-2 border-orange-200" data-testid={`dir-photo-${u.id}`} />;
  }
  const initials = u.name.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase();
  return <div className="w-16 h-16 rounded-full bg-orange-50 border-2 border-orange-200 flex items-center justify-center font-display font-bold text-orange-700 text-lg" data-testid={`dir-initials-${u.id}`}>{initials}</div>;
};

export default function Directory() {
  const { data: staff } = useQuery({ queryKey: ['staff-directory'], queryFn: () => api.get('/people/staff-directory').then((r) => r.data) });
  const { data: branding } = useQuery({ queryKey: ['branding'], queryFn: () => api.get('/branding').then((r) => r.data) });
  const [q, setQ] = useState('');
  const [role, setRole] = useState('all');
  const roles = useMemo(() => [...new Set((staff || []).map((s) => s.role))], [staff]);
  const filtered = (staff || []).filter((s) =>
    (role === 'all' || s.role === role) &&
    (!q || `${s.name} ${s.email} ${s.phone}`.toLowerCase().includes(q.toLowerCase())));

  return (
    <div data-testid="directory-page">
      <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight mb-1">Staff Directory</h1>
      <p className="text-sm text-slate-500 mb-6">Every staff & faculty member - photos, contacts and background-verification status</p>

      <div className="flex flex-wrap items-center gap-2 mb-6">
        <Input data-testid="directory-search-input" placeholder="Search by name, email or phone..." value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs" />
        <button data-testid="dir-filter-all" onClick={() => setRole('all')}
          className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${role === 'all' ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-200 hover:border-slate-900'}`}>All ({(staff || []).length})</button>
        <button data-testid="dir-print-all" onClick={() => filtered.length && printIdCards(filtered, branding)}
          className="text-xs rounded-full px-3 py-1.5 border border-orange-300 text-orange-700 hover:bg-orange-50 transition-colors flex items-center gap-1.5">
          <IdentificationCard size={14} /> Print {filtered.length} ID card{filtered.length === 1 ? '' : 's'}
        </button>
        {roles.map((r) => (
          <button key={r} data-testid={`dir-filter-${r}`} onClick={() => setRole(r)}
            className={`text-xs rounded-full px-3 py-1.5 border transition-colors ${role === r ? 'bg-orange-600 text-white border-orange-600' : 'border-slate-200 hover:border-orange-500'}`}>
            {ROLE_LABEL[r] || r} ({(staff || []).filter((s) => s.role === r).length})
          </button>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="directory-grid">
        {filtered.map((u) => (
          <div key={u.id} className="bg-white border border-slate-200 rounded-md p-4 card-hover stagger-in" data-testid={`dir-card-${u.id}`}>
            <div className="flex items-start gap-3">
              <Avatar u={u} />
              <div className="min-w-0">
                <p className="font-display font-bold truncate">{u.name}</p>
                <p className="font-mono-label text-[10px] uppercase text-orange-600">{ROLE_LABEL[u.role] || u.role}</p>
                <span className={`inline-flex items-center gap-1 font-mono-label text-[9px] uppercase px-1.5 py-0.5 rounded mt-1 ${BGV_STYLE[u.bgv_status] || BGV_STYLE.not_started}`} data-testid={`dir-bgv-${u.id}`}>
                  <ShieldCheck size={10} /> BGV {u.bgv_status.replace(/_/g, ' ')}{u.bgv_valid_until ? ` · ${u.bgv_valid_until}` : ''}
                </span>
              </div>
            </div>
            <div className="border-t border-slate-100 mt-3 pt-2 space-y-1 text-xs text-slate-600">
              <p className="flex items-center gap-1.5 truncate"><EnvelopeSimple size={12} className="text-slate-400 shrink-0" /> {u.email}</p>
              {u.phone && <p className="flex items-center gap-1.5"><Phone size={12} className="text-slate-400 shrink-0" /> {u.phone}</p>}
              {u.status !== 'active' && <span className="font-mono-label text-[9px] uppercase bg-slate-100 text-slate-500 rounded px-1.5 py-0.5">{u.status}</span>}
              <button data-testid={`dir-idcard-${u.id}`} onClick={() => printIdCards([u], branding)} className="text-xs underline text-orange-700 flex items-center gap-1 pt-1"><IdentificationCard size={12} /> Print ID card</button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-sm text-slate-400 col-span-4 py-6">No staff match this filter.</p>}
      </div>
    </div>
  );
}
